import { ConfirmModal } from '@affine/component/ui/modal';
import {
  openQuotaModalAtom,
  openSettingModalAtom,
} from '@affine/core/components/atoms';
import { UserQuotaService } from '@affine/core/modules/cloud';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import { WorkspaceQuotaService } from '@affine/core/modules/quota';
import { FeatureName } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import {
  useLiveData,
  useServices,
  WorkspaceService,
} from '@toeverything/infra';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';

export const CloudQuotaModal = () => {
  const t = useI18n();

  const {
    workspaceService,
    workspaceQuotaService,
    workspacePermissionService,
    userQuotaService,
  } = useServices({
    WorkspaceService,
    WorkspaceQuotaService,
    WorkspacePermissionService,
    UserQuotaService,
  });

  const currentWorkspace = workspaceService.workspace;
  const [open, setOpen] = useAtom(openQuotaModalAtom);

  useEffect(() => {
    workspaceQuotaService.quota.revalidate();
  }, [workspaceQuotaService]);
  const workspaceQuota = useLiveData(workspaceQuotaService.quota.quota$);

  const isOwner = useLiveData(workspacePermissionService.permission.isOwner$);
  useEffect(() => {
    workspacePermissionService.permission.revalidate();
  }, [workspacePermissionService]);

  const userQuota = useLiveData(
    userQuotaService.quota.quota$.map(q =>
      q
        ? {
            feature: q.name,
            blobLimit: q.displayFormat.blobLimit,
          }
        : null
    )
  );

  const isFreePlanOwner = useMemo(() => {
    return isOwner && userQuota?.feature === FeatureName.free_plan_v1;
  }, [isOwner, userQuota]);

  const setSettingModalAtom = useSetAtom(openSettingModalAtom);

  const handleUpgradeConfirm = useCallback(() => {
    if (!isOwner) {
      return;
    }

    track.$.paywall.storage.viewPlans();
    setOpen(false);

    setSettingModalAtom({
      open: true,
      activeTab: 'plans',
    });
  }, [isOwner, setOpen, setSettingModalAtom]);

  const description = useMemo(() => {
    if (userQuota && isFreePlanOwner) {
      return t['com.affine.payment.blob-limit.description.owner.free']({
        planName: t['com.affine.payment.cloud.free.name'](),
        currentQuota: userQuota.blobLimit,
      });
    }
    if (
      isOwner &&
      userQuota &&
      userQuota.feature === FeatureName.basic_plan_v1
    ) {
      return t['com.affine.payment.blob-limit.description.owner.basic']({
        planName: t['com.affine.payment.cloud.basic.name'](),
        quota: userQuota.blobLimit,
      });
    }
    if (isOwner && userQuota && userQuota.feature === FeatureName.pro_plan_v1) {
      return t['com.affine.payment.blob-limit.description.owner.pro']({
        planName: t['com.affine.payment.cloud.pro.name'](),
        quota: userQuota.blobLimit,
      });
    }
    if (workspaceQuota) {
      return t['com.affine.payment.blob-limit.description.member']({
        quota: workspaceQuota.displayFormat.blobLimit,
      });
    } else {
      // loading
      return null;
    }
  }, [userQuota, isFreePlanOwner, isOwner, workspaceQuota, t]);

  useEffect(() => {
    if (!workspaceQuota) {
      return;
    }

    currentWorkspace.engine.blob.getBlobSizeLimit = async () => {
      const quotaBlob = await workspaceQuotaService.quota.getQuotaBlob();
      return quotaBlob.blobLimit;
    };

    const disposable = currentWorkspace.engine.blob.onAbortLargeBlob.on(() => {
      setOpen(true);
    });
    return () => {
      disposable?.dispose();
    };
  }, [
    currentWorkspace.engine.blob,
    setOpen,
    workspaceQuota,
    workspaceQuotaService.quota,
  ]);

  return (
    <ConfirmModal
      open={open}
      title={t['com.affine.payment.blob-limit.title']()}
      onOpenChange={setOpen}
      description={description}
      cancelButtonOptions={{
        hidden: !isFreePlanOwner,
      }}
      onConfirm={handleUpgradeConfirm}
      confirmText={
        isFreePlanOwner ? t['com.affine.payment.upgrade']() : t['Got it']()
      }
      cancelText={t['docnboard.payment.blob-limit.cancel']()}
      confirmButtonOptions={{
        variant: 'primary',
      }}
    />
  );
};
